import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Container,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import {
  GetContactFromEmail,
  getMailListsForContact,
  UnsubscribeFromList,
} from "../../api/canmail/CANmailAPI";
import {
  Unsubscribe as UnsubscribeIcon,
  CheckCircle,
  Error,
} from "@mui/icons-material";

function Unsubscribe() {
  const location = useLocation();
  const [email, setEmail] = useState(null);
  const [contactID, setContactID] = useState(null);
  const [mailingLists, setMailingLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unsubscribing, setUnsubscribing] = useState({});
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    const initializeUnsubscribe = async () => {
      try {
        // Extract email from URL
        const params = new URLSearchParams(location.search);
        let emailParam = params.get("email");
        if (!emailParam) {
          setError(
            "No email parameter found in the URL. Please contact support for assistance."
          );
          setLoading(false);
          return;
        }

        // Decode email (handle URL encoding)
        let decEmail = emailParam.replace("%40", "@");
        setEmail(decEmail);

        // Get contact ID from email
        const contactData = await GetContactFromEmail(decEmail);
        if (!contactData) {
          setError(
            "Email address not found in our system. Please contact support for assistance."
          );
          setLoading(false);
          return;
        }

        // Handle case where API returns a JSON string that needs to be parsed
        let parsedContactData;
        if (typeof contactData === "string") {
          try {
            parsedContactData = JSON.parse(contactData);
          } catch (parseError) {
            console.error("Error parsing contact data:", parseError);
            setError(
              "Invalid response format. Please contact support for assistance."
            );
            setLoading(false);
            return;
          }
        } else {
          parsedContactData = contactData;
        }

        // Handle case where API returns an array of contact IDs
        let contactID;
        if (Array.isArray(parsedContactData)) {
          // Select the first contact ID from the array
          contactID = parsedContactData[0];
        } else if (parsedContactData.ContactID) {
          // Handle case where API returns an object with ContactID property
          contactID = parsedContactData.ContactID;
        } else {
          setError(
            "Email address not found in our system. Please contact support for assistance."
          );
          setLoading(false);
          return;
        }

        setContactID(contactID);

        // Get mailing lists for contact
        const listsData = await getMailListsForContact(contactID);
        console.log("listsData: ", listsData);

        let parsedListsData;
        if (typeof listsData === "string") {
          try {
            parsedListsData = JSON.parse(listsData);
          } catch (parseError) {
            console.error("Error parsing lists data:", parseError);
            setMailingLists([]);
          }
        } else {
          parsedListsData = listsData;
        }

        if (parsedListsData && Array.isArray(parsedListsData)) {
          console.log("allLists: ", parsedListsData);
          setMailingLists(parsedListsData);
        } else {
          setMailingLists([]);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error initializing unsubscribe:", error);
        setError(
          "An error occurred while loading your subscription information. Please try again or contact support."
        );
        setLoading(false);
      }
    };

    initializeUnsubscribe();
  }, [location.search]);

  const handleUnsubscribe = async (listID, listName) => {
    if (!contactID) return;

    setUnsubscribing((prev) => ({ ...prev, [listID]: true }));
    setSuccessMessage(null);

    try {
      await UnsubscribeFromList(contactID, listID);

      // Remove the list from the local state
      setMailingLists((prev) => prev.filter((list) => list.list_id !== listID));

      setSuccessMessage(`Successfully unsubscribed from "${listName}"`);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error) {
      console.error("Error unsubscribing:", error);
      setError("Failed to unsubscribe. Please try again or contact support.");

      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000);
    } finally {
      setUnsubscribing((prev) => ({ ...prev, [listID]: false }));
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="60vh"
          gap={3}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading your subscription information...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="60vh"
          gap={3}
        >
          <Error color="error" sx={{ fontSize: 60 }} />
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            {error}
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
             <Box
         display="flex"
         flexDirection="column"
         alignItems="center"
         justifyContent="center"
         minHeight="60vh"
         mt={4}
       >
         <Card elevation={3} sx={{ width: "100%", maxWidth: 600 }}>
           <CardContent sx={{ p: 4 }}>
             {/* Header */}
             <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
               <Typography variant="h5" component="h1" gutterBottom textAlign="center">
                 Unsubscribe from Mailing Lists
               </Typography>
               <Chip
                 label={
                   <Typography variant="body2" component="span">
                     {email}
                   </Typography>
                 }
                 size="small"
                 variant="outlined"
               />
             </Box>

            {/* Success Message */}
            {successMessage && (
              <Alert
                severity="success"
                icon={<CheckCircle />}
                sx={{ mb: 3 }}
                onClose={() => setSuccessMessage(null)}
              >
                {successMessage}
              </Alert>
            )}

            {/* Mailing Lists */}
            {mailingLists.length === 0 ? (
              <Box textAlign="center" py={4}>
                <CheckCircle color="success" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  There are no active subscriptions for this email address.
                </Typography>
              </Box>
            ) : (
              <>
                <Paper variant="outlined" sx={{ mt: 2 }}>
                  <List>
                    {mailingLists.map((list, index) => (
                      <React.Fragment key={list.list_id}>
                                                 <ListItem
                           sx={{
                             display: "flex",
                             flexDirection: { xs: "column", sm: "row" },
                             justifyContent: { xs: "flex-start", sm: "space-between" },
                             alignItems: { xs: "stretch", sm: "center" },
                             py: 2,
                             gap: { xs: 2, sm: 0 },
                           }}
                         >
                           <ListItemText
                             primary={
                               <Box display="flex" alignItems="center" gap={1}>
                                 <Typography
                                   variant="subtitle1"
                                   fontWeight="medium"
                                 >
                                   {list.list_name}
                                 </Typography>
                               </Box>
                             }
                           />
                           <Button
                             variant="outlined"
                             color="error"
                             startIcon={
                               unsubscribing[list.list_id] ? (
                                 <CircularProgress size={16} />
                               ) : (
                                 <UnsubscribeIcon />
                               )
                             }
                             onClick={() =>
                               handleUnsubscribe(list.list_id, list.list_name)
                             }
                             disabled={unsubscribing[list.list_id]}
                             sx={{ 
                               minWidth: { xs: "100%", sm: 120 },
                               width: { xs: "100%", sm: "auto" }
                             }}
                           >
                             {unsubscribing[list.list_id]
                               ? "Unsubscribing..."
                               : "Unsubscribe"}
                           </Button>
                         </ListItem>
                        {index < mailingLists.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>

                <Box mt={3} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    You can resubscribe to these lists at any time by contacting
                    our support team.
                  </Typography>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}

export default Unsubscribe;
